import { TbCheck, TbCopy } from "solid-icons/tb";
import type { Component } from "solid-js";
import { Show, createSignal } from "solid-js";
import { twMerge } from "tailwind-merge";
import { HighlightedCodeBlock } from "../code-highlighting/HighlightedCodeBlock";
import type { CodeBlockASTNode } from "../toastmark/ast";

export interface CodeBlockWithCopyProps {
  node: CodeBlockASTNode;
  class?: string;
  showCopyButton?: boolean;
}

/**
 * Code block component with GitHub-style syntax highlighting and copy-to-clipboard functionality.
 * Features:
 * - GitHub light/dark theme syntax highlighting
 * - Copy button visible on hover
 * - Icon feedback (TbCopy -> TbCheck) with 1.5s timeout
 * - Supports streaming code blocks
 */
export const CodeBlockWithCopy: Component<CodeBlockWithCopyProps> = (props) => {
  const [copied, setCopied] = createSignal(false);

  const handleCopy = async (e: MouseEvent) => {
    e.stopPropagation();
    const code = props.node.literal || "";

    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error("Failed to copy code:", error);
    }
  };

  const containerClass = twMerge("relative group prism-github prism-github-dark", props.class);

  const buttonClass = twMerge(
    "absolute top-2 right-2 z-10",
    "p-1.5 rounded-lg",
    "bg-tile hover:bg-tile-hover active:bg-tile-hover",
    "border border-primary-border shadow-sm",
    "transition-all duration-200",
    "focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1",
    "opacity-0 group-hover:opacity-100",
  );

  return (
    <div class={containerClass}>
      <Show when={props.showCopyButton !== false}>
        <button
          type="button"
          onClick={handleCopy}
          class={buttonClass}
          title={copied() ? "Copied!" : "Copy to clipboard"}
          aria-label={copied() ? "Copied!" : "Copy to clipboard"}
        >
          <Show
            when={copied()}
            fallback={<TbCopy class="w-4 h-4 text-secondary-text hover:text-primary-text transition-colors" />}
          >
            <TbCheck class="w-4 h-4 text-white" />
          </Show>
        </button>
      </Show>
      <HighlightedCodeBlock node={props.node} class="p-2" />
    </div>
  );
};
